import { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { Save, RotateCcw, Plus, Trash2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL || ''}/api`;

const getAuthHeaders = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem('admin_token')}` }
});

export const AdminServices = () => {
  const [content, setContent] = useState({
    tagline: '',
    title: '',
    private_lessons: { title: '', icon: '', description: '', features: [], extra_info: '' },
    school_teaching: { title: '', icon: '', description: '', features: [] },
    method_title: '',
    method_text: []
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchContent();
  }, []);

  const fetchContent = async () => {
    try {
      const response = await axios.get(`${API}/content/services`);
      setContent(response.data);
    } catch (error) {
      toast.error('Failed to load content');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/content/services`, { ...content, id: 'services' }, getAuthHeaders());
      toast.success('Services saved!');
    } catch (error) {
      toast.error('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (field, value) => {
    setContent(prev => ({ ...prev, [field]: value }));
  };

  const handleServiceChange = (service, field, value) => {
    setContent(prev => ({
      ...prev,
      [service]: { ...prev[service], [field]: value }
    }));
  };

  const handleFeatureChange = (service, index, value) => {
    const newFeatures = [...content[service].features];
    newFeatures[index] = value;
    handleServiceChange(service, 'features', newFeatures);
  };

  const addFeature = (service) => {
    const newFeatures = [...content[service].features, ''];
    handleServiceChange(service, 'features', newFeatures);
  };

  const removeFeature = (service, index) => {
    const newFeatures = content[service].features.filter((_, i) => i !== index);
    handleServiceChange(service, 'features', newFeatures);
  };

  const handleMethodChange = (index, value) => {
    const newText = [...content.method_text];
    newText[index] = value;
    setContent(prev => ({ ...prev, method_text: newText }));
  };

  const addMethodParagraph = () => {
    setContent(prev => ({ ...prev, method_text: [...prev.method_text, ''] }));
  };

  const removeMethodParagraph = (index) => {
    setContent(prev => ({ ...prev, method_text: prev.method_text.filter((_, i) => i !== index) }));
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div></div>;
  }

  return (
    <div data-testid="admin-services">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-serif">Services</h1>
          <p className="text-muted-foreground">Edit service offerings</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={fetchContent} disabled={saving}>
            <RotateCcw className="w-4 h-4 mr-2" /> Reset
          </Button>
          <Button onClick={handleSave} disabled={saving} data-testid="save-services-btn">
            <Save className="w-4 h-4 mr-2" /> {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        </div>
      </div>

      <div className="space-y-6">
        {/* Section Info */}
        <div className="bg-white rounded-lg border border-border p-6 space-y-4">
          <h2 className="font-semibold">Section Info</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-2">Tagline</label>
              <input
                type="text"
                value={content.tagline}
                onChange={(e) => handleChange('tagline', e.target.value)}
                className="w-full px-4 py-3 border border-border rounded-lg focus:border-primary focus:outline-none"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Title</label>
              <input
                type="text"
                value={content.title}
                onChange={(e) => handleChange('title', e.target.value)}
                className="w-full px-4 py-3 border border-border rounded-lg focus:border-primary focus:outline-none"
              />
            </div>
          </div>
        </div>

        {/* Private Lessons */}
        <div className="bg-white rounded-lg border border-border p-6">
          <h2 className="font-semibold mb-4">Private Lessons</h2>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2">Title</label>
              <input
                type="text"
                value={content.private_lessons.title}
                onChange={(e) => handleServiceChange('private_lessons', 'title', e.target.value)}
                className="w-full px-4 py-3 border border-border rounded-lg focus:border-primary focus:outline-none"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Description</label>
              <textarea
                value={content.private_lessons.description}
                onChange={(e) => handleServiceChange('private_lessons', 'description', e.target.value)}
                rows={3}
                className="w-full px-4 py-3 border border-border rounded-lg focus:border-primary focus:outline-none resize-none"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Extra Info</label>
              <input
                type="text"
                value={content.private_lessons.extra_info || ''}
                onChange={(e) => handleServiceChange('private_lessons', 'extra_info', e.target.value)}
                className="w-full px-4 py-3 border border-border rounded-lg focus:border-primary focus:outline-none"
                placeholder="e.g., Based in Yateley..."
              />
            </div>
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-sm font-medium">Features</label>
                <Button variant="outline" size="sm" onClick={() => addFeature('private_lessons')}>
                  <Plus className="w-4 h-4 mr-1" /> Add
                </Button>
              </div>
              {content.private_lessons.features.map((f, i) => (
                <div key={i} className="flex gap-2 mb-2">
                  <input
                    type="text"
                    value={f}
                    onChange={(e) => handleFeatureChange('private_lessons', i, e.target.value)}
                    className="flex-1 px-3 py-2 text-sm border border-border rounded focus:border-primary focus:outline-none"
                  />
                  <Button variant="ghost" size="icon" onClick={() => removeFeature('private_lessons', i)} className="text-red-500">
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* School Teaching */}
        <div className="bg-white rounded-lg border border-border p-6">
          <h2 className="font-semibold mb-4">School Teaching</h2>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2">Title</label>
              <input
                type="text"
                value={content.school_teaching.title}
                onChange={(e) => handleServiceChange('school_teaching', 'title', e.target.value)}
                className="w-full px-4 py-3 border border-border rounded-lg focus:border-primary focus:outline-none"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Description</label>
              <textarea
                value={content.school_teaching.description}
                onChange={(e) => handleServiceChange('school_teaching', 'description', e.target.value)}
                rows={3}
                className="w-full px-4 py-3 border border-border rounded-lg focus:border-primary focus:outline-none resize-none"
              />
            </div>
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-sm font-medium">Features</label>
                <Button variant="outline" size="sm" onClick={() => addFeature('school_teaching')}>
                  <Plus className="w-4 h-4 mr-1" /> Add
                </Button>
              </div>
              {content.school_teaching.features.map((f, i) => (
                <div key={i} className="flex gap-2 mb-2">
                  <input
                    type="text"
                    value={f}
                    onChange={(e) => handleFeatureChange('school_teaching', i, e.target.value)}
                    className="flex-1 px-3 py-2 text-sm border border-border rounded focus:border-primary focus:outline-none"
                  />
                  <Button variant="ghost" size="icon" onClick={() => removeFeature('school_teaching', i)} className="text-red-500">
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Method */}
        <div className="bg-white rounded-lg border border-border p-6">
          <h2 className="font-semibold mb-4">The Method</h2>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2">Title</label>
              <input
                type="text"
                value={content.method_title}
                onChange={(e) => handleChange('method_title', e.target.value)}
                className="w-full px-4 py-3 border border-border rounded-lg focus:border-primary focus:outline-none"
              />
            </div>
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-sm font-medium">Paragraphs</label>
                <Button variant="outline" size="sm" onClick={addMethodParagraph}>
                  <Plus className="w-4 h-4 mr-1" /> Add
                </Button>
              </div>
              {content.method_text.map((p, i) => (
                <div key={i} className="flex gap-2 mb-2">
                  <textarea
                    value={p}
                    onChange={(e) => handleMethodChange(i, e.target.value)}
                    rows={2}
                    className="flex-1 px-3 py-2 text-sm border border-border rounded focus:border-primary focus:outline-none resize-none"
                  />
                  <Button variant="ghost" size="icon" onClick={() => removeMethodParagraph(i)} className="text-red-500">
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminServices;
